import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Service, inject } from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Entity, EntityKey} from '../entity/entity';
import {EntityHeader} from '../entity/entity-header';
import {EntitySearchCriteria} from '../entity/entity-search-criteria';
import {PartSearchResult} from '../products/part-search-result';
import {Riser} from '../products/riser';
import {GoogleService} from './google-service';
import {CreditDocument} from "../documents/document";
import { UnrestrictedEntityUpdate } from '../model/unrestricted-entity-update';

@Service()
export class EntityService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl;

  public addParts(entityId: string, entityRevision: number, parts: PartSearchResult): Observable<Entity> {
    let url = this.urlBase + 'entity/addParts/' + entityId + '/' + entityRevision;
    let body = JSON.stringify(parts);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public addRisers(key: EntityKey, risers: Riser[]): Observable<Entity> {
    let url = this.urlBase + 'entity/addRisers/' + key.entityId + '/' + key.entityRevision;
    let body = JSON.stringify(risers);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  public getList(criteria: EntitySearchCriteria): Observable<EntityHeader[]> {
    let url = this.urlBase + 'entity/list';
    let body = JSON.stringify(criteria);
    return this.http.post<EntityHeader[]>(url, body, {headers: this.getPostHeaders()});
  }

  public getListTemplate(criteria: EntitySearchCriteria): Observable<Blob> {
    let url = this.urlBase + 'entity/list/template';
    let body = JSON.stringify(criteria);
    return this.http.post(url, body, {
      headers: this.getPostHeaders(),
      observe: 'response',
      responseType: 'blob'
    }).pipe(map((response: any) => {
      return new Blob([response.body], {type: response.headers.get('Content-Type')});
    }));
  }

  public getEntityByKey(key: EntityKey): Observable<Entity> {
    return this.getEntity(key.entityName, key.entityId, key.entityRevision);
  }

  public getEntity(name: string, id: string, revision: number): Observable<Entity> {
    let url = this.urlBase + 'entity/' + name + '/' + id + '/' + revision;
    return this.http.get<Entity>(url, {headers: this.getRequestHeaders()});
  }

  public cancel(key: EntityKey, reasonCode: string): Observable<Entity> {
    let url = this.urlBase + 'entity/cancel/' + key.entityId + '/' + key.entityRevision + '/' + reasonCode;
    return this.http.post<Entity>(url, '', {headers: this.getPostHeaders()});
  }

  public fixPermissions(key: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'entity/fixPermissions';
    let body = JSON.stringify(key);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public hasSkyportCharacteristic(key: EntityKey): Observable<string> {
    let url = this.urlBase + 'entity/hasUSSkyportCharacteristic/' + key.entityName
      + '/' + key.entityId + '/' + key.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  public isLineWithSkyportMissingRoom(key: EntityKey): Observable<string> {
    let url = this.urlBase + 'entity/isLineWithSkyportMissingRoom/' + key.entityName
      + '/' + key.entityId + '/' + key.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  public getCreditDocuments(key: EntityKey): Observable<CreditDocument[]> {
    let url = this.urlBase + 'entity/creditDocuments/' + key.entityId + '/' + key.entityRevision;
    return this.http.get<CreditDocument[]>(url, {headers: this.getRequestHeaders()});
  }

  isPartsOrWarranty(entity: Entity): boolean {
    return !!(entity && entity.leadTimeCode && ('PTS' === entity.leadTimeCode || 'WAR' === entity.leadTimeCode || 'SLC' === entity.leadTimeCode));
  }

  hasMissingRiserData(key: EntityKey): Observable<string> {
    const url = this.urlBase + 'entity/hasMissingRiserData/' + key.entityName + '/' + key.entityId + '/' + key.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  saveEntityWithoutRestrictions(update: UnrestrictedEntityUpdate): Observable<Entity> {
    const url: string = this.urlBase + 'entity/unrestrictedUpdate';
    const body: string = JSON.stringify(update);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

}
